import { Modal, ModalBody, TextArea, Dropdown } from '@carbon/react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './RemarkDialog.scss';
import { InlineNotification } from '@carbon/react';
import { InformationFilled } from '@carbon/icons-react';

// Define the template interface
interface RemarkTemplate {
  id: string;
  text: string;
  category: string;
}

// Define the comparison status interface
interface ComparisonStatus {
  direct?: {
    status: 'ok' | 'warning' | 'error';
    difference: number;
  };
  indirect?: {
    status: 'ok' | 'warning' | 'error';
    difference: number;
  };
  activity?: {
    status: 'ok' | 'warning' | 'error';
    difference: number;
  };
}

interface FormCompletionProps {
  onClose: () => void;
  onSubmitWithRemarks: (remarks: string) => void;
  comparisonStatus?: ComparisonStatus;
  hasRenewableEnergy?: boolean;
  renewablePercentage?: number;
}

/**
 * RemarkDialog component for collecting user remarks when emissions exceed thresholds.
 * 
 * Required translation keys:
 * - survey.submission.modalData.remarkText
 * - survey.submission.modalData.remarkPlaceholder
 * - survey.submission.modalData.remarkInvalid
 * - survey.submission.modalData.remarkRequired
 * - survey.submission.modalData.charactersNeeded
 * - survey.submission.modalData.characterCount
 * - survey.submission.modalData.remarkTooLong
 * - survey.submission.modalData.presetRemarks
 * - survey.submission.modalData.selectTemplate
 * - survey.submission.modalData.selectTemplatePrompt
 * - survey.submission.modalData.addTemplate
 * - survey.submission.validation.errorTitle
 * - survey.submission.validation.errorSubtitle
 * - survey.submission.validation.issuesFound
 * - survey.submission.validation.renewableEnergyNote
 * 
 * Remark categories:
 * - survey.submission.remarkCategories.technology
 * - survey.submission.remarkCategories.process
 * - survey.submission.remarkCategories.energy
 * - survey.submission.remarkCategories.materials
 * - survey.submission.remarkCategories.methodology
 * - survey.submission.remarkCategories.showAll
 * 
 * Remark templates:
 * - survey.submission.remarkTemplates.outdatedTechnology - e.g. "The EU default values are based on outdated technology assumptions; our equipment was installed in 2022 or later."
 * - survey.submission.remarkTemplates.processOptimizations - e.g. "Region-specific process optimizations and high process integration yield significantly reduced emission intensity."
 * - survey.submission.remarkTemplates.renewableEnergy - e.g. "The EU default does not reflect our country's decarbonized grid or on-site renewable energy installations."
 * - survey.submission.remarkTemplates.materialEfficiency - e.g. "Our advanced material efficiency measures reduce emissions beyond what is assumed in default values."
 * - survey.submission.remarkTemplates.measurementMethodology - e.g. "We use more accurate measurement methodologies than those assumed in the default calculations."
 */

const RemarkDialog: React.FC<FormCompletionProps> = ({
  onClose,
  onSubmitWithRemarks,
  comparisonStatus,
  hasRenewableEnergy = false,
  renewablePercentage = 0
}) => {
  const { t } = useTranslation();
  const [remarks, setRemarks] = useState('');
  const MAX_REMARKS_LENGTH = 512;
  const MIN_REMARKS_LENGTH = 100;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pre-made remark templates from translation files
  const remarkTemplates: RemarkTemplate[] = [
    
    {
      id: 'materialEfficiency',
      text: t('survey.submission.remarkTemplates.materialEfficiency'),
      category: t('survey.submission.remarkCategories.materials')
    },
    {
      id: 'measurementMethodology',
      text: t('survey.submission.remarkTemplates.measurementMethodology'),
      category: t('survey.submission.remarkCategories.methodology')
    },
    {
      id: 'outdatedTechnology',
      text: t('survey.submission.remarkTemplates.outdatedTechnology'),
      category: t('survey.submission.remarkCategories.technology')
    },
    {
      id: 'processOptimizations',
      text: t('survey.submission.remarkTemplates.processOptimizations'),
      category: t('survey.submission.remarkCategories.process')
    },
    {
      id: 'renewableEnergy',
      text: t('survey.submission.remarkTemplates.renewableEnergy'),
      category: t('survey.submission.remarkCategories.energy')
    }
  ];

  const currentLength = remarks.trim().length;
  const isValid = currentLength >= MIN_REMARKS_LENGTH && currentLength <= MAX_REMARKS_LENGTH;
  const remainingChars = MAX_REMARKS_LENGTH - currentLength;

  // Generate a simple summary of validation issues if available
  const getValidationSummary = () => {
    if (!comparisonStatus) return null;
    
    const issues = [];
    
    if (comparisonStatus.direct?.status === 'warning') {
      const diff = comparisonStatus.direct.difference;
      issues.push(`Direct emissions ${diff > 0 ? 'higher' : 'lower'} than default by ${Math.abs(diff).toFixed(1)}%`);
    }
    
    if (comparisonStatus.indirect?.status === 'warning') {
      const diff = comparisonStatus.indirect.difference;
      issues.push(`Indirect emissions ${diff > 0 ? 'higher' : 'lower'} than default by ${Math.abs(diff).toFixed(1)}%`);
    }
    
    if (comparisonStatus.activity?.status === 'warning') {
      const diff = comparisonStatus.activity.difference;
      issues.push(`Activity ${diff > 0 ? 'higher' : 'lower'} than production by ${Math.abs(diff).toFixed(1)}%`);
    }
    
    return issues;
  };
  
  const validationIssues = getValidationSummary();

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handlePrimaryClick = () => {
    if (!isValid || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      onSubmitWithRemarks(remarks);
    } catch (error) {
      console.error('Error in form submission:', error);
      setIsSubmitting(false);
    }
  };

  const handleSecondaryClick = () => {
    if (isSubmitting) return; 
    onClose();
  };

  // Add template text to current remarks
  const addTemplate = (template: string) => {
    // Check if adding the template would exceed the max length
    if (remarks.length + template.length <= MAX_REMARKS_LENGTH) {
      // If remarks already has content, add a space before the template
      const newRemarks = remarks ? `${remarks} ${template}` : template;
      setRemarks(newRemarks);
    }
  };

  // Helper to get the appropriate helper text
  const getHelperText = () => {
    if (currentLength === 0) {
      return t('survey.submission.modalData.remarkRequired');
    } else if (currentLength < MIN_REMARKS_LENGTH) {
      return `${t('survey.submission.modalData.charactersNeeded')} (${currentLength}/${MIN_REMARKS_LENGTH})`;
    } else {
      return `${t('survey.submission.modalData.characterCount')}: ${currentLength}/${MAX_REMARKS_LENGTH}`;
    }
  };

  const isOverMaxLimit = currentLength > MAX_REMARKS_LENGTH;

  return (
    <Modal
      open={true}
      onClose={onClose}
      modalLabel={t('survey.submission.validationRemark.errorTitle')}
      modalHeading={t('survey.submission.validationRemark.errorSubtitle')}
      primaryButtonText="Submit"
      secondaryButtonText="Back to Survey"
      primaryButtonDisabled={!isValid}
      onRequestClose={onClose}
      onRequestSubmit={() => isValid && onSubmitWithRemarks(remarks)}
      onSecondarySubmit={handleSecondaryClick}
      hasScrollingContent={false}
      passiveModal={false}
    >
      <ModalBody>
        <div className="flex flex-col items-center justify-center p-4">
          {/* Add validation summary if available */}
          {validationIssues && validationIssues.length > 0 && (
            <div className="validation-summary mb-4 w-full">
              <InlineNotification
                kind="warning"
                lowContrast
                title={t('survey.submission.validationRemark.issuesFound')}
                subtitle={
                  <div className="mt-2">
                    <ul className="list-disc pl-4">
                      {validationIssues.map((issue, index) => (
                        <li key={index} className="mb-1">{issue}</li>
                      ))}
                    </ul>
                    
                    {hasRenewableEnergy && (
                      <p className="mt-2">
                        <InformationFilled className="inline-block mr-1" size={16} />
                        {t('survey.submission.validationRemark.renewableEnergyNote', { percentage: renewablePercentage.toFixed(1) })}
                      </p>
                    )}
                  </div>
                }
                hideCloseButton
              />
            </div>
          )}
          
          {/* Pre-made templates section - simplified to dropdown only */}
          <div className="pre-made-templates mb-4 w-full">
            <p className="text-sm font-medium mb-2">{t('survey.submission.modalData.presetRemarks')}</p>
            
            {/* Template dropdown for selection */}
            <div className="template-dropdown">
              <Dropdown<RemarkTemplate>
                id="template-selector"
                titleText={t('survey.submission.modalData.selectTemplate')}
                label={t('survey.submission.modalData.selectTemplatePrompt')}
                items={remarkTemplates}
                itemToString={(item: RemarkTemplate | null) => item ? item.text : ''}
                onChange={({ selectedItem }: { selectedItem: RemarkTemplate | null }) => {
                  if (selectedItem) {
                    addTemplate(selectedItem.text);
                  }
                }}
                className="w-full"
              />
            </div>
          </div>
        
          <div className="remarks-section mt-4 w-full">
            <TextArea
              labelText={t('survey.submission.modalData.remarkText')}
              placeholder={t('survey.submission.modalData.remarkPlaceholder')}
              value={remarks}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setRemarks(e.target.value)}
              invalid={isOverMaxLimit || (currentLength > 0 && currentLength < MIN_REMARKS_LENGTH)}
              invalidText={
                isOverMaxLimit 
                  ? t('survey.submission.modalData.remarkTooLong') + ` (${currentLength}/${MAX_REMARKS_LENGTH})`
                  : currentLength > 0 && currentLength < MIN_REMARKS_LENGTH
                    ? t('survey.submission.modalData.remarkInvalid') + ` (${currentLength}/${MIN_REMARKS_LENGTH})`
                    : undefined
              }
              helperText={getHelperText()}
              maxLength={MAX_REMARKS_LENGTH}
            />
            
            {/* Character count indicator - visual progress */}
            <div className="character-counter mt-2">
              <div className="progress-container w-full h-1 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`progress-bar h-full ${
                    isOverMaxLimit 
                      ? 'bg-red-500' 
                      : isValid 
                        ? 'bg-green-500' 
                        : 'bg-blue-500'
                  }`}
                  style={{ 
                    width: `${Math.min(100, (currentLength / MIN_REMARKS_LENGTH) * 100)}%`,
                    opacity: currentLength > MIN_REMARKS_LENGTH ? 
                      Math.max(0.4, 1 - ((currentLength - MIN_REMARKS_LENGTH) / (MAX_REMARKS_LENGTH - MIN_REMARKS_LENGTH))) : 1
                  }}
                />
              </div>
              {/* Remaining characters display */}
              <div className="text-right text-xs text-gray-500 mt-1">
                {remainingChars < 50 ? (
                  <span className={remainingChars < 0 ? 'text-red-500 font-bold' : 'text-amber-600'}>
                    {remainingChars} characters remaining
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default RemarkDialog;